import { useEffect, useState } from 'react'
import { Button, Chip, SearchField } from '@heroui/react'
import { Notice } from '../admin/settingsKit'
import type { SignageScreen } from '../lib/api'
import { branchZone, changeMoment } from '../lib/signageEdit'
import { agoOf } from '../lib/signageViews'
import { useStaffText } from '../ui/StaffText'
import { Icon } from '../ui/icons'
import type { SignageData } from './signageData'
import { Empty, TileSkeletons } from './signageKit'
import { Thumb } from './signageLibrary'

/** How often the tab asks again while it is open. */
const REFRESH_MS = 30_000

/** Every main display the person reaches: on or off, when last seen, and what it plays now. */
export function ScreensTab({ data, canMachines, onNavigate }: { data: SignageData; canMachines: boolean; onNavigate: (to: string) => void }) {
  const { say } = useStaffText()
  const [find, setFind] = useState('')
  const [now, setNow] = useState(() => Date.now())
  const { reload } = data
  useEffect(() => {
    void reload('screens')
    const t = window.setInterval(() => {
      setNow(Date.now())
      void reload('screens')
    }, REFRESH_MS)
    return () => window.clearInterval(t)
  }, [reload])

  const q = find.trim().toLowerCase()
  const shown = data.screens.filter((s) => !q || `${s.name} ${s.code} ${s.branchName}`.toLowerCase().includes(q))
  const online = data.screens.filter((s) => s.online).length

  return (
    <section className="signage-screens" aria-label={say('signage.tab.screens')}>
      {data.screens.length > 0 && (
        <div className="signage-bar">
          <SearchField className="signage-find" aria-label={say('signage.findScreen')} value={find} onChange={setFind} variant="secondary">
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={say('signage.findScreen')} />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <span className="signage-tally">
            <span>
              <i className="signage-status" data-on="true" aria-hidden="true" /> {say('signage.nOnline', { n: online })}
            </span>
            <span>
              <i className="signage-status" aria-hidden="true" /> {say('signage.nOffline', { n: data.screens.length - online })}
            </span>
          </span>
          <span className="signage-bar__gap" />
          <Button
            size="sm"
            variant="secondary"
            onPress={() => {
              setNow(Date.now())
              void reload('screens')
            }}
          >
            <Icon name="refresh" size={14} /> {say('signage.refresh')}
          </Button>
        </div>
      )}
      {data.faults.screens && <Notice>{data.faults.screens}</Notice>}
      {!data.loaded.screens ? (
        <TileSkeletons n={4} className="signage-screengrid" />
      ) : data.screens.length === 0 ? (
        !data.faults.screens && (
          <div className="signage-dropzone">
            <Empty
              icon="designs"
              title={say('signage.noScreens')}
              action={
                canMachines ? (
                  <Button variant="secondary" size="sm" onPress={() => onNavigate('/administration#branches')}>
                    {say('signage.openBranches')}
                  </Button>
                ) : undefined
              }
            />
          </div>
        )
      ) : shown.length === 0 ? (
        <Empty icon="search" title={say('signage.noMatch', { q: find.trim() })} />
      ) : (
        <ul className="signage-screengrid">
          {shown.map((s) => (
            <li key={s.id}>
              <ScreenCard screen={s} now={now} zone={branchZone(data.branches.find((b) => b.id === s.branchId)?.timezone)} canMachines={canMachines} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function ScreenCard({ screen: s, now, zone, canMachines, onNavigate }: { screen: SignageScreen; now: number; zone: string; canMachines: boolean; onNavigate: (to: string) => void }) {
  const { say, lang } = useStaffText()
  const ago = agoOf(s.seenAt, now)
  const seen = ago ? say(ago.key, { n: ago.n }) : say('signage.seenNever')
  const source =
    s.campaigns.length > 0 ? s.campaigns.map((c) => c.name).join(', ') : s.items.length > 0 ? say('signage.now.design') : say('signage.now.nothing')
  const first = s.items[0]
  return (
    <article className="signage-screen" data-online={s.online || undefined}>
      <div className="signage-screen__face">
        {first ? (
          <Thumb media={first} className="signage-face" />
        ) : (
          <span className="signage-face">
            <Icon name="designs" size={24} />
          </span>
        )}
        <span className="signage-screen__pill" data-on={s.online || undefined}>
          <i className="signage-status" data-on={s.online || undefined} aria-hidden="true" />
          {s.online ? say('signage.online') : say('signage.offline')}
        </span>
        {s.items.length > 1 && <span className="signage-badge">{say('signage.nItems', { n: s.items.length })}</span>}
      </div>
      <div className="signage-screen__body">
        <span className="signage-screen__branch">
          {s.branchName} · {s.branchCode}
          {!s.seat && (
            <Chip size="sm" variant="soft" color="warning">
              {say('signage.state.noSeat')}
            </Chip>
          )}
        </span>
        <div className="signage-screen__title">
          <span className="signage-screen__name" title={s.name}>
            {s.name}
          </span>
          <span className="signage-muted">{s.code}</span>
        </div>
        <span className="signage-muted">{ago ? say('signage.lastSeen', { when: seen }) : seen}</span>
        <dl className="signage-facts signage-facts--tight">
          <div className="signage-fact">
            <dt>{say('signage.playsNow')}</dt>
            <dd>
              <strong>{source}</strong>
              {s.changesAt && <span className="signage-muted"> · {say('signage.now.until', { time: changeMoment(s.changesAt, new Date(now), lang, zone) })}</span>}
            </dd>
          </div>
          <div className="signage-fact">
            <dt>{say('signage.design')}</dt>
            <dd>
              {s.design ? (
                <button type="button" className="signage-link" onClick={() => onNavigate(`/screens#${s.design!.id}`)}>
                  {s.design.name}
                </button>
              ) : (
                <span className="signage-muted">{say('signage.noDesign')}</span>
              )}
            </dd>
          </div>
        </dl>
        {s.design && !s.playlistArea && (
          <span className="signage-warn">
            <Icon name="alert" size={13} /> {say('signage.noArea')}
          </span>
        )}
        {s.skipped && (
          <span className="signage-warn" title={s.skipped.name}>
            <Icon name="alert" size={13} /> {say('signage.skipped', { n: s.skipped.count })}
          </span>
        )}
        {canMachines && (
          <button type="button" className="signage-link signage-screen__machine" onClick={() => onNavigate(`/administration#branches/${encodeURIComponent(s.branchCode)}`)}>
            {say('signage.machine')} <Icon name="arrowUpRight" size={12} />
          </button>
        )}
      </div>
    </article>
  )
}
